import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api, { invalidateSession } from '@/utils/api';
import { useCartStore } from '@/stores/cart';
import { normalizePermissions } from '@/utils/permissions';

/**
 * v8.3.0 P3-08（T05 + T22）：身份与权限状态。
 *
 * 权限点的唯一来源是服务端（server/src/common/permissions.ts 的 permissionsFor）：
 * 登录响应 user.permissions（T05）+ GET /auth/profile（T05）两条都是同一个出口。
 * 前端只存、只用，不再按 role 自己算一遍矩阵。
 *
 * 三条容易被忽略但直接决定线上表现的口径：
 * 1) 【persist】permissions 必须跟着 user 一起写 localStorage。否则刷新页面后内存里
 *    只剩 role，页面判定退回 isAdmin/isManager，等于旧行为没变。
 * 2) 【老缓存】升级前登录过的账号，localStorage.user 里【没有 permissions 字段】。
 *    这时把缺失当成「空数组」= 把店主降级成收银员（菜单全没、还不报错）。
 *    正确做法是区分「缺失」与「空」：缺失 → 调 GET /auth/profile 回读真值；空 → 收银员。
 * 3) 【fail-closed】回读失败 / 无权限字段时 permissions 保持为空，can() 一律 false。
 *    宁可少显示一个入口，也不能让越权按钮点出去再吃 403。
 */
export const useAuthStore = defineStore('auth', () => {
  /** 读 localStorage.user，解析失败按未登录处理（旧缓存被手工改坏时不能白屏） */
  function readCachedUser(): any {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  const token = ref(localStorage.getItem('token') || '');
  const user = ref(readCachedUser());
  const currentStoreId = ref(localStorage.getItem('currentStoreId') || '');

  /**
   * 权限点数组。
   * 初值取 local storage 缓存里 user.permissions（新登录写进来的），老缓存取不到就是 []。
   * 注意 [] 在这里只是「还没拿到」，不代表「没有任何权限」——
   * 是否已确认由 permissionsResolved 单独表达，避免用空数组承载两种语义。
   */
  const permissions = ref<string[]>(normalizePermissions(user.value?.permissions) || []);

  /**
   * 权限是否已由服务端确认过。
   * false = 老缓存 / 刚登录但响应里没有 permissions（老后端）→ 需要 ensurePermissions() 回读。
   */
  const permissionsResolved = ref<boolean>(normalizePermissions(user.value?.permissions) !== null);

  const isLoggedIn = computed(() => !!token.value);
  // 保留三个角色判断：显示名（「总部管理员 / 收银员」标签）与 24 处旧代码还在用，
  // 页面能不能做某事逐步改成 can()，这两个不再承担权限判定的职责。
  const isAdmin = computed(() => user.value?.role === 1);
  const isManager = computed(() => user.value?.role === 2);
  const isCashier = computed(() => user.value?.role === 3);
  const storeId = computed(() => user.value?.storeId);
  /** T05 下发的首登强制改密标记（T08 用来跳改密页），老缓存没有则按 false */
  const mustChangePassword = computed(() => user.value?.mustChangePassword === true);

  /** user 落盘：permissions 一起存，刷新页面不丢 */
  function persistUser() {
    try {
      if (user.value) localStorage.setItem('user', JSON.stringify(user.value));
      else localStorage.removeItem('user');
    } catch {
      /* localStorage 不可用（隐私模式）时忽略，内存里仍可用 */
    }
  }

  async function login(phone: string, password: string) {
    const res = await api.post('/auth/login', { phone, password });
    setAuth(res.data);
    await ensurePermissions();
    return res.data;
  }

  async function sendCode(phone: string) {
    const res = await api.post('/auth/send-code', { phone });
    // P2-4：与 login/verifyCode 一致地返回解包后的实际数据（{ message, code }），
    // 不再返回整层 backendResponse，消除调用方的心智负担
    return res.data;
  }

  async function verifyCode(phone: string, code: string) {
    const res = await api.post('/auth/verify-code', { phone, code });
    setAuth(res.data);
    await ensurePermissions();
    return res.data;
  }

  function setAuth(data: any) {
    // R-07：登录即开启新会话代，任何上一个账号在途的刷新结果都会被丢弃
    invalidateSession();
    // v8.2.5：购物车已提升到 pinia（跨组件、跨路由存活），登录换人时必须清空，
    // 否则上一位收银员未结算的商品会带到新账号名下（少收/错收）。
    useCartStore().clear();
    // R-07 修复（P2）：invalidateSession() 只清了 localStorage 的 currentStoreId，
    // Pinia 里这条 ref 仍是上一个账号选的门店 → 换账号后 Home.vue 显示上一位的门店名、
    // Stores.vue 的「当前」标记错位、ADMIN 多店选择被丢。这里同步重置为初始值，
    // 与 logout() 的处理保持一致（组件里的门店上下文必须跟着登录身份一起换）。
    currentStoreId.value = '';
    token.value = data.accessToken;
    // v8.3.0：permissions 从登录响应带进来（T05）。用 normalizePermissions 区分
    // 「缺失（老后端）」与「空数组（收银员）」：缺失时不写成 []，交给 ensurePermissions 回读，
    // 否则老后端 + 老账号会被固定成收银员视图。
    const incoming = normalizePermissions(data?.user?.permissions);
    user.value = incoming === null
      ? { ...(data?.user || {}) }
      : { ...(data?.user || {}), permissions: incoming };
    permissions.value = incoming === null ? [] : incoming;
    permissionsResolved.value = incoming !== null;
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    persistUser();
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
  }

  /**
   * 用 GET /auth/profile（T05）的返回把身份与权限补齐。
   * 这是「老缓存不重新登录也能拿到真实权限」的唯一兜底路径。
   * 注意合并而不是覆盖：profile 不含 accessToken 之类，只更新身份字段；
   * 门店名 storeName 一并带回，前端不用再查一次。
   */
  function applyProfile(profile: any) {
    if (!profile || typeof profile !== 'object') return false;
    const incoming = normalizePermissions(profile.permissions);
    if (incoming === null) {
      // profile 也没有 permissions（老后端 / 网关截断）→ 保持不可信状态，can() 继续 false
      return false;
    }
    user.value = { ...(user.value || {}), ...profile, permissions: incoming };
    permissions.value = incoming;
    permissionsResolved.value = true;
    persistUser();
    return true;
  }

  /**
   * 主动回读权限。已确认过就直接返回 true，不重复打接口。
   * 任何失败都吞掉并返回 false：调用方拿到 false 时应按 fail-closed 处理，
   * 不能因为一次网络抖动把人踢下线（token 是否过期由 api 拦截器统一处理）。
   *
   * 【并发去重】：初始化时会自动触发一次回读，页面组件又可能各自再调一次
   * （例如多个页面同时 onMounted）。不加单飞的话一次刷新会打多个 /auth/profile，
   * 低配服务器上纯属浪费，而且每多一次就多一次失败面。这里用 in-flight promise 去重，
   * 与 api.ts 的 refreshPromise 同一套思路。
   */
  let permissionPromise: Promise<boolean> | null = null;
  async function ensurePermissions(): Promise<boolean> {
    if (permissionsResolved.value) return true;
    if (!token.value) return false;
    if (permissionPromise) return permissionPromise;
    permissionPromise = (async () => {
      try {
        const res: any = await api.get('/auth/profile');
        // 兼容两种响应形态：后端 TransformInterceptor 包了一层 { code, message, data }，
        // 若某环境未包装则直接是载荷本身。
        const payload = res?.data?.data || res?.data || res || {};
        return applyProfile(payload);
      } catch {
        return false;
      } finally {
        permissionPromise = null;
      }
    })();
    return permissionPromise;
  }

  function setCurrentStore(storeId: string) {
    currentStoreId.value = storeId;
    localStorage.setItem('currentStoreId', storeId);
  }

  /**
   * R-07：签退 / 退出登录。
   * 除了清空 store 与 localStorage，还会作废任何在途的静默刷新（会话代递增），
   * 保证签退之后不会有旧账号的令牌被异步写回，下一位收银员不会继承上一位的身份。
   */
  function logout() {
    invalidateSession();
    // v8.2.5：签退 / 退出登录必须清空购物车。购物车提升到 pinia 后不再随 POS 组件
    // 卸载而消失，若只清 POS 页内的会话，从「我的」页退出时会把未结算商品留给下一位。
    useCartStore().clear();
    token.value = '';
    user.value = null;
    currentStoreId.value = '';
    // v8.3.0：权限必须跟着身份一起清空，否则下一位登录前 can() 仍残留上一账号的权限
    permissions.value = [];
    permissionsResolved.value = false;
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentStoreId');
    delete api.defaults.headers.common['Authorization'];
  }

  // Restore token on init
  if (token.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`;
    // v8.3.0：老缓存（v8.2.5 及更早登录过、localStorage.user 里没有 permissions）
    // 不清缓存直接刷新页面时，这里自动回读一次真实权限。不阻塞应用启动：
    // 页面首帧按 fail-closed 隐藏受限入口，回读成功后 permissions 变化会自动放出来。
    if (user.value && !permissionsResolved.value) {
      void ensurePermissions();
    }
  }

  return {
    token,
    user,
    currentStoreId,
    permissions,
    permissionsResolved,
    isLoggedIn,
    isAdmin,
    isManager,
    isCashier,
    storeId,
    mustChangePassword,
    login,
    sendCode,
    verifyCode,
    setAuth,
    applyProfile,
    setCurrentStore,
    ensurePermissions,
    logout,
  };
});
